'use client'
import * as React from 'react'
import { useIsMounted } from 'hooks/use-is-mounted'
import { tw } from 'utils/classnames'

interface RootProps extends React.HTMLProps<HTMLDivElement> {
  defaultOpen?: boolean
}

export function Root({ children, defaultOpen, ...props }: RootProps) {
  const { containerRef, textWrapperRef, isContentCropped } = useContentOverflow()
  const [open, setOpen] = React.useState(defaultOpen ?? false)

  const state = React.useMemo(
    () => ({
      containerRef,
      textWrapperRef,
      isContentCropped,
      open,
      setOpen,
    }),
    [containerRef, textWrapperRef, isContentCropped, open, setOpen],
  )

  return (
    <Context.Provider value={state}>
      <Container {...props}>{children}</Container>
    </Context.Provider>
  )
}

function useContentOverflow() {
  const [isContentCropped, setIsContentCropped] = React.useState(false)
  const containerRef = React.useRef<HTMLDivElement>(null)
  const textWrapperRef = React.useRef<HTMLSpanElement>(null)
  const isLoaded = useIsMounted()

  React.useEffect(() => {
    const checkContentOverflow = () => {
      if (containerRef.current && textWrapperRef.current) {
        const div = containerRef.current
        const span = textWrapperRef.current
        const isOverflowing = span.clientHeight > div.clientHeight
        setIsContentCropped(isOverflowing)
      }
    }

    checkContentOverflow()

    window.addEventListener('resize', checkContentOverflow)
    return () => {
      window.removeEventListener('resize', checkContentOverflow)
    }
  }, [])

  return { containerRef, textWrapperRef, isLoaded, isContentCropped }
}

interface ContainerProps extends React.HTMLProps<HTMLDivElement> {
  className?: string
}

export function Container({ children, className, ...rest }: ContainerProps) {
  const { open, isContentCropped } = useContext()
  return (
    <div
      data-id="prod-description"
      className={tw(
        'relative w-full',
        !open &&
          isContentCropped &&
          'after:to-cf-white after:rounded-b-4 after:from-cf-white/30 after:pointer-events-none after:absolute after:bottom-0 after:left-0 after:z-0 after:h-20 after:w-full after:bg-gradient-to-b',
        className,
      )}
      {...rest}
    >
      <div className={tw('mb-4', !isContentCropped && 'mb-0', 'h-full overflow-hidden')}>
        {children}
      </div>
    </div>
  )
}

type ContentProps = React.HTMLProps<HTMLDivElement>

export function Content({ children, className, ...rest }: ContentProps) {
  const { containerRef, open, textWrapperRef } = useContext()

  return (
    <div
      ref={containerRef}
      className={tw(
        open
          ? 'line-clamp-none'
          : 'line-clamp-5 max-h-[calc(4*24px)] min-[992px]:line-clamp-3 min-[992px]:max-h-[calc(2*24px)]',
        open ? 'mb-4' : 'mb-2',
        'h-full transition-all',
        className,
      )}
      {...rest}
    >
      <span ref={textWrapperRef} className="inline-block">
        {children}
      </span>
    </div>
  )
}

export interface State {
  containerRef?: React.RefObject<HTMLDivElement | null>
  textWrapperRef?: React.RefObject<HTMLSpanElement | null>
  isContentCropped: boolean
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
}

export const Context = React.createContext<State | undefined>(undefined)

export function useContext() {
  const context = React.useContext(Context)
  if (!context) {
    throw new Error('useContext must be used within a ContextProvider')
  }
  return context
}

interface TitleProps extends React.HTMLProps<HTMLDivElement> {
  className?: string
}

export function Title({ children, className, ...rest }: TitleProps) {
  return (
    <div className={tw('mb-2 text-[18px] font-bold leading-7', className)} {...rest}>
      {children}
    </div>
  )
}

interface ExpandibleButtonProps extends React.HTMLProps<HTMLButtonElement> {
  showMoreText?: string
  showLessText?: string
  showIcon?: boolean
}

export function TriggerButton({
  showMoreText,
  showLessText,
  showIcon,
  className,
  ...rest
}: ExpandibleButtonProps) {
  const { open, setOpen, isContentCropped } = useContext()

  if (!isContentCropped) {
    return null
  }

  return (
    <button
      className={tw(
        'text-14 absolute bottom-0 z-10 flex w-[calc(100%-2*16px)] items-center justify-center py-4 text-left font-semibold min-[960px]:justify-start',
        className,
      )}
      {...rest}
      type="button"
      onClick={() => {
        setOpen?.(!open)
      }}
    >
      <span className="inline-block">{open ? showLessText : showMoreText}</span>
      {showIcon ? (
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className={tw(
            'ml-2 inline-block transition-transform',
            open ? '-rotate-90' : 'rotate-90',
          )}
        >
          <g id="chevron_right">
            <path
              id="Vector"
              d="M6.47003 4L5.53003 4.94L8.58336 8L5.53003 11.06L6.47003 12L10.47 8L6.47003 4Z"
              fill="currentColor"
            />
          </g>
        </svg>
      ) : null}
    </button>
  )
}
